import {Axios} from 'services/axios';

export const sectionContentApi = {
  /**
   * Get section by ID with full details
   * @param {string} sectionId - Section ID
   * @returns {Promise} - Section details
   */
  getSectionById: async sectionId => {
    try {
      if (!sectionId) {
        throw new Error('Section ID is required');
      }

      const response = await Axios.fetch(`sections/${sectionId}`);

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching section:', error);
      let errorMessage = 'Failed to fetch section';
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = 'Section not found';
        } else {
          errorMessage =
            error.response.data?.message ||
            `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }
      throw new Error(errorMessage);
    }
  },

  /**
   * Update section content and settings with image uploads
   * @param {string} sectionId - Section ID
   * @param {FormData} formData - FormData with content, settings and files
   * @returns {Promise} - Updated section data
   */
  updateSection: async (sectionId, formData) => {
    try {
      if (!sectionId) {
        throw new Error('Section ID is required');
      }

      const response = await Axios.put(`sections/${sectionId}`, formData, {
        // headers: {
        'Content-Type': 'multipart/form-data',
        // },
      });

      return response.data.data || response.data;
    } catch (error) {
      console.error('❌ Error updating section:', error);
      let errorMessage = 'Failed to update section';
      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }
      throw new Error(errorMessage);
    }
  },

  /**
   * Get section version history
   * @param {string} sectionId - Section ID
   * @param {Object} options - Pagination options
   * @returns {Promise} - Version history
   */
  getSectionVersions: async (sectionId, options = {}) => {
    try {
      if (!sectionId) {
        throw new Error('Section ID is required');
      }

      const {page = 1, limit = 20} = options;
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      const response = await Axios.fetch(
        `sections/${sectionId}/versions?${params}`,
      );

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching section versions:', error);
      let errorMessage = 'Failed to fetch section versions';
      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }
      throw new Error(errorMessage);
    }
  },

  /**
   * Restore section to specific version
   * @param {string} sectionId - Section ID
   * @param {string} versionId - Version ID to restore
   * @param {string} changeReason - Reason for restore
   * @returns {Promise} - Restored section
   */
  restoreSectionVersion: async (sectionId, versionId, changeReason = '') => {
    try {
      if (!sectionId || !versionId) {
        throw new Error('Section ID and Version ID are required');
      }

      const response = await Axios.post(
        `sections/${sectionId}/restore/${versionId}`,
        {
          changeReason: changeReason || `Restored from version ${versionId}`,
        },
      );

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error restoring section version:', error);
      let errorMessage = 'Failed to restore section version';
      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }
      throw new Error(errorMessage);
    }
  },

  /**
   * Get version details
   * @param {string} versionId - Version ID
   * @returns {Promise} - Version details
   */
  getVersionDetails: async versionId => {
    try {
      if (!versionId) {
        throw new Error('Version ID is required');
      }

      const response = await Axios.fetch(`sections/versions/${versionId}`);

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching version details:', error);
      let errorMessage = 'Failed to fetch version details';
      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }
      throw new Error(errorMessage);
    }
  },
  
  /**
   * Preview section
   * @param {string} sectionId - Section ID
   * @param {Object} data - Current form data to preview
   * @returns {Promise} - Preview HTML/data
   */
  previewSection: async (sectionId, data) => {
    try {
      if (!sectionId) {
        throw new Error('Section ID is required');
      }

      const response = await Axios.post(`sections/${sectionId}/preview`, data, {
        'Content-Type': 'application/json',
      });

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error previewing section:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to preview section',
      );
    }
  },

  /**
   * Transform section data for form
   * @param {Object} section - Section data from API
   * @returns {Object} - Transformed data for form
   */
  transformSectionDataForForm: section => {
    if (!section) return {content: {}, settings: {}};

    return {
      id: section.id,
      content: section.content || {},
      settings: section.settings || {
        padding: {top: 'medium', bottom: 'medium'},
        bgColor: '#ffffff',
        textAlign: 'left',
        containerWidth: 'full',
        customCss: '',
      },
      pageId: section.pageId,
      templateId: section.templateId,
      sectionOrder: section.sectionOrder,
      isActive: section.isActive,
      isVisible: section.isVisible,
      status: section.status,
      version: section.version,
      createdAt: section.createdAt,
      updatedAt: section.updatedAt,
      template: section.template,
      page: section.page,
    };
  },

  /**
   * Prepare section data for API
   * @param {Object} formData - Form data from component
   * @returns {FormData} - Prepared FormData for API
   */
  prepareSectionForApi: (formData, imageFiles = {}, galleryFilesMap = {}) => {
    const fd = new FormData();

    // Content and settings as JSON
    fd.append('content', JSON.stringify(formData.content || {}));
    fd.append('settings', JSON.stringify(formData.settings || {}));

    if (formData.changeSummary) {
      fd.append('changeSummary', formData.changeSummary);
    }

    // Image files
    Object.entries(imageFiles).forEach(([key, file]) => {
      if (file instanceof File) {
        fd.append(`image_${key}`, file);
      }
    });

    // Gallery files
    Object.entries(galleryFilesMap).forEach(([key, files]) => {
      files.forEach(file => {
        if (file instanceof File) {
          fd.append(`gallery_${key}`, file);
        }
      });
    });

    return fd;
  },
};

export default sectionContentApi;
